import { useTranslation } from "react-i18next";
import styled from "styled-components";
import { PaymentMethodType } from "@KassaroAB/shared/types/enums/PaymentMethodType";
import type { RestGetReceiptPayment } from "@KassaroAB/shared/types/receiptPayment";
import { formatMoney } from "../../../utils/formatMoney";
import colors from "@styling/colors";
import { spacing } from "@styling/sizing";
import { fontSizes } from "@styling/fonts";

const PaymentBlock = styled.div`
  margin-bottom: ${spacing.x2}px;
`;

const PaymentHeader = styled.div`
  display: flex;
  justify-content: space-between;
  gap: ${spacing.x2}px;
  font-size: ${fontSizes.regular}px;
  color: ${colors.text};
`;

const PaymentDetails = styled.div`
  margin-top: ${spacing.x05}px;
  font-size: ${fontSizes.small}px;
  color: ${colors.textMuted};
  display: flex;
  flex-direction: column;
  gap: ${spacing.x05}px;
`;

interface ReceiptPaymentProps {
  payment: RestGetReceiptPayment;
}

export const ReceiptPayment = ({ payment }: ReceiptPaymentProps) => {
  const { t } = useTranslation();
  const label =
    payment.name ||
    t(`paymentMethodType.${payment.type}`, { defaultValue: payment.type });

  const details = renderPaymentDetails(payment, t);

  return (
    <PaymentBlock>
      <PaymentHeader>
        <span>{label}:</span>
        <span>{formatMoney(payment.amount)}</span>
      </PaymentHeader>
      {details.length > 0 && (
        <PaymentDetails>
          {details.map((line) => (
            <div key={line}>{line}</div>
          ))}
        </PaymentDetails>
      )}
    </PaymentBlock>
  );
};

const renderPaymentDetails = (
  payment: RestGetReceiptPayment,
  t: (key: string, options?: Record<string, unknown>) => string,
): string[] => {
  if (
    payment.type === PaymentMethodType.WESTPAY_CLOUD &&
    payment.cardPaymentInfo
  ) {
    const info = payment.cardPaymentInfo;
    const lines = [
      info.transactionType,
      info.cardholderObscuredPan,
      info.cardName.trim(),
      info.isContactless ? "CONTACTLESS" : null,
      info.pinEntered ? "PIN" : null,
      info.verificationMethod === "ONLINE_PIN" ? "ONLINE_PIN" : null,
      info.verificationMethod === "SIGNATURE" ? "SIGNATURE" : null,
      `Betalkod: ${info.responseCode}`,
      info.debitCreditSelection.trim() !== "NONE" ? info.debitCreditSelection : null,
      info.cardHolderString,
      `Ref.nr: ${info.transactionId}`,
      `AID: ${info.aid}`,
      `TVR: ${info.tvr}`,
      `TSI: ${info.tsi}`,
      `Acq Ref: ${info.storeIdObscured ?? info.storeId}`,
      `TerminalID: ${info.terminalIdObscured ?? info.terminalId}`,
    ];
    return lines.filter((line): line is string => Boolean(line));
  }

  if (
    (payment.type === PaymentMethodType.MARKETPAY_CLOUD ||
      payment.type === PaymentMethodType.MARKETPAY_LOCAL) &&
    payment.cardPaymentInfo
  ) {
    const info = payment.cardPaymentInfo;
    return [
      `Ref.nr: ${info.transactionId}`,
      `TerminalID: ${info.terminalId}`,
      `MerchantID: ${info.merchantId}`,
      `AuthCode: ${info.authorizationCode}`,
      info.cardData ? `PAN: ${info.cardData.extractedPan}` : null,
      info.cardData ? `AID: ${info.cardData.applicationId}` : null,
      `Status: ${info.status}`,
      info.signatureRequired ? "SIGNATURE" : null,
      info.cardData ? info.cardData.cardCapture : null,
    ].filter((line): line is string => Boolean(line));
  }

  if (payment.type === PaymentMethodType.SOFTPAY && payment.cardPaymentInfo) {
    const info = payment.cardPaymentInfo;
    return [
      info.partialPan ?? null,
      info.scheme ?? null,
      info.type ?? null,
      info.auditNumber ? `Ref.nr: ${info.auditNumber}` : null,
      info.aid ? `AID: ${info.aid}` : null,
      info.storeId ? `Acq Ref: ${info.storeId}` : null,
      info.acquirerTerminalId ? `TerminalID: ${info.acquirerTerminalId}` : null,
      info.acquirerId ? `AcquirerID: ${info.acquirerId}` : null,
      info.acquirerName ? `Acquirer: ${info.acquirerName}` : null,
      info.acquirerProcessor ? `Processor: ${info.acquirerProcessor}` : null,
      info.cvm ? `CVM: ${info.cvm}` : null,
    ].filter((line): line is string => Boolean(line));
  }

  if (payment.type === PaymentMethodType.CASH) {
    if (payment.receivedAmount || payment.changeAmount) {
      return [
        payment.receivedAmount
          ? `${t("receiptsView.detail.paymentDetails.receivedAmount")} ${formatMoney(payment.receivedAmount)}`
          : null,
        payment.changeAmount
          ? `${t("receiptsView.detail.paymentDetails.changeAmount")} ${formatMoney(payment.changeAmount)}`
          : null,
      ].filter((line): line is string => Boolean(line));
    }
  }

  if (
    (payment.type === PaymentMethodType.VIVA ||
      payment.type === PaymentMethodType.VIVA_CLOUD ||
      payment.type === PaymentMethodType.VIVA_SMART_CHECKOUT) &&
    payment.cardPaymentInfo
  ) {
    const info = payment.cardPaymentInfo;
    return [
      [info.verificationMethod, info.cardType].filter(Boolean).join(" ") ||
        null,
      info.cardholderObscuredPan ?? null,
      `STAN: ${info.stan}`,
      `TID: ${info.tid}`,
      `RRN: ${info.rrn}`,
      `AUTH: ${info.authorizationCode}`,
      `TTC: ${info.transactionDate}`,
      `AID: ${info.aid}`,
      `OrderCode: ${info.orderCode}`,
    ].filter((line): line is string => Boolean(line));
  }

  if (payment.type === PaymentMethodType.NETS_TERMINAL && payment.cardPaymentInfo) {
    const info = payment.cardPaymentInfo;
    return [
      info.cardIssuerName ?? null,
      info.accountNumberMasked ?? null,
      `AID: ${info.aid}`,
      `TVR: ${info.tvr}`,
      `REF: ${info.stanAuth} ${info.tcc}`,
      `RESP: ${info.responseCode}`,
      `PERIOD: ${info.sessionNumber}`,
    ].filter((line): line is string => Boolean(line));
  }

  if (payment.type === PaymentMethodType.INVOICE) {
    return [
      `${t("receiptsView.detail.paymentDetails.invoiceNumber")}${payment.invoiceId}`,
      payment.reference
        ? `${t("receiptsView.detail.paymentDetails.reference")} ${payment.reference}`
        : null,
      payment.dueDate
        ? `${t("receiptsView.detail.paymentDetails.dueDate")} ${payment.dueDate}`
        : null,
      payment.customerName
        ? `${t("receiptsView.detail.paymentDetails.customer")} ${payment.customerName}`
        : null,
      payment.ocr ? `${t("receiptsView.detail.paymentDetails.ocr")} ${payment.ocr}` : null,
      payment.orgNumber
        ? `${t("receiptsView.detail.paymentDetails.orgNumber")} ${payment.orgNumber}`
        : null,
    ].filter((line): line is string => Boolean(line));
  }

  if (payment.type === PaymentMethodType.ORDER) {
    return [
      `${t("receiptsView.detail.paymentDetails.orderNumber")}${payment.orderId}`,
      payment.reference
        ? `${t("receiptsView.detail.paymentDetails.reference")} ${payment.reference}`
        : null,
      payment.customerName
        ? `${t("receiptsView.detail.paymentDetails.customer")} ${payment.customerName}`
        : null,
      payment.orgNumber
        ? `${t("receiptsView.detail.paymentDetails.orgNumber")} ${payment.orgNumber}`
        : null,
    ].filter((line): line is string => Boolean(line));
  }

  return [];
};
